import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DwComponent, DwComponentType } from '@devwareapps/devware-cap';

import {
  CoverageModuleVm,
  CoverageQuestionVm,
  CoverageStatus,
  CoverageTotals,
  CoverageVideoVm,
  QuizFixDraftResponse,
  QuizVideoMatchData
} from '../../models/quiz-video-coverage.model';
import {
  buildCoverageView,
  CoverageFilter,
  CoverageView,
  COVERAGE_GROUPS,
  COVERAGE_SCOPE_NOTE,
  COVERAGE_STATUSES,
  CoverageLegendItem,
  countTotals,
  filterQuestions,
  FIX_LEGEND,
  SPELLING_LEGEND
} from './quiz-video-coverage.util';

type CoverageTab = 'questions' | 'modules' | 'videos';

/** The parts of HTMLDialogElement used here; the DOM typings bundled with this TypeScript version may lack it. */
interface ModalDialog extends HTMLElement {
  showModal(): void;
  close(): void;
}

interface Option<T> {
  key: T;
  label: string;
}

/** One drafted fix, shaped for display. */
interface DraftVersionVm {
  key: string;
  question: string;
  answers: { key: string; text: string; correct: boolean }[];
  reason: string;
  /** e.g. 'TypeSafe: 99% likely the meaning is unchanged'. */
  checkLabel: string;
  checkOk: boolean;
  copyText: string;
}

type DraftKind = 'text' | 'reword' | 'align';

interface DraftState {
  kind: DraftKind;
  loading: boolean;
  error: string;
  versions: DraftVersionVm[];
  model: string;
}

interface StatusKpi {
  key: CoverageStatus;
  label: string;
  icon: string;
  count: number;
  /** e.g. '51%'; '<1%' when the count is non-zero but rounds to 0. */
  percentLabel: string;
  active: boolean;
  /** Stacked-bar segment tooltip, e.g. 'Taught before quiz: 968 (44%)'. */
  title: string;
}

/**
 * Prototype: quiz question ↔ course video coverage from the TypeSafe matching script.
 * Results contain correct answers, so they are never bundled: the JSON is read from a gitignored
 * local asset when present, otherwise picked from disk.
 */
@DwComponent({
  componentType: DwComponentType.pageComponent,
  key: 'quiz-video-coverage-page',
  name: 'Quiz Video Coverage (Prototype)',
  isGlobal: true
})
@Component({
  selector: 'app-quiz-video-coverage-page',
  templateUrl: './quiz-video-coverage-page.component.html',
  styleUrls: ['./quiz-video-coverage-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuizVideoCoveragePageComponent implements OnInit {
  /** Local result runs, newest first; both stay gitignored because they contain correct answers. */
  private static readonly datasets: { key: string; label: string; url: string }[] = [
    { key: 'v8', label: 'v8 · Placement scope', url: 'assets/prototypes/quiz-video-matches-v8.json' },
    { key: 'v7', label: 'v7 · Evidence and scope', url: 'assets/prototypes/quiz-video-matches.json' }
  ];
  /** Prototype-only drafting proxy (AI drafts, TypeSafe checks); see scripts/quiz-builder-ai-proxy.js. */
  private static readonly draftFixUrl = 'http://127.0.0.1:4310/draft-fix';
  /** A draft whose TypeSafe check is under this is shown with a warning. */
  private static readonly draftCheckOk = 0.8;

  readonly statuses = COVERAGE_STATUSES;
  readonly spellingDescription = SPELLING_LEGEND.description;
  readonly scopeNote = COVERAGE_SCOPE_NOTE;
  /** Legend rows, grouped like the summary tiles; misspelling is its own row because it overlaps any status. */
  readonly legendGroups: { key: string; label: string; items: CoverageLegendItem[] }[] = [
    ...COVERAGE_GROUPS.map(g => ({ ...g, items: COVERAGE_STATUSES.filter(s => s.group === g.key) })),
    { key: 'text', label: 'Question text', items: [SPELLING_LEGEND] },
    { key: 'fixes', label: 'Suggested fixes', items: FIX_LEGEND }
  ];
  /** Badge whose explanation the legend shows. */
  legendItem: CoverageLegendItem = COVERAGE_STATUSES[0];
  /** Every legend entry, rendered together in the detail panel so its height never changes. */
  readonly legendItems: CoverageLegendItem[] = this.legendGroups.reduce((all, g) => all.concat(g.items), [] as CoverageLegendItem[]);
  readonly tabs: Option<CoverageTab>[] = [
    { key: 'questions', label: 'Questions' },
    { key: 'modules', label: 'Modules' },
    { key: 'videos', label: 'Videos' }
  ];
  readonly datasetOptions: Option<string>[] = QuizVideoCoveragePageComponent.datasets.map(d => ({ key: d.key, label: d.label }));
  /** Active result run; '' when a file was picked from disk instead. */
  datasetKey = 'v8';
  private readonly datasetCache = new Map<string, QuizVideoMatchData>();

  isLoading = true;
  hasData = false;
  loadError = '';
  meta = '';
  tab: CoverageTab = 'questions';
  filter: CoverageFilter = { courseId: null, moduleId: null, status: null, spelling: false, search: '' };

  courseOptions: Option<number>[] = [];
  moduleOptions: Option<number>[] = [];
  totals: CoverageTotals = {
    total: 0, covered: 0, misplaced: 0, reworded: 0, partial: 0, move: 0, earlier: 0, uncovered: 0, noQuiz: 0, noTranscript: 0, misspelled: 0
  };
  spellingPercentLabel = '0%';
  kpis: StatusKpi[] = [];
  kpiGroups: { key: string; label: string; items: StatusKpi[] }[] = [];
  stackLabel = '';
  questions: CoverageQuestionVm[] = [];
  modules: CoverageModuleVm[] = [];
  videos: CoverageVideoVm[] = [];
  untestedVideoCount = 0;
  selected: CoverageQuestionVm | null = null;
  /** Draft for the selected question; drafts are kept per question for the session. */
  draft: DraftState | null = null;
  copiedKey = '';
  private readonly drafts = new Map<number, DraftState>();
  wordingOpen = false;
  shownLabel = '';

  @ViewChild('legendDialog', { static: true }) private readonly legendDialog: ElementRef<ModalDialog>;

  private view: CoverageView | null = null;
  private allModuleOptions: (Option<number> & { courseId: number })[] = [];

  constructor(private readonly http: HttpClient, private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.loadDataset(this.datasetKey, true);
  }

  /** Switches between the local v7/v8 result runs; parsed data is cached so switching back is instant. */
  setDataset(key: string): void {
    if (key !== this.datasetKey) {
      this.loadDataset(key, false);
    }
  }

  private loadDataset(key: string, initial: boolean): void {
    const dataset = QuizVideoCoveragePageComponent.datasets.find(d => d.key === key);
    if (!dataset) {
      return;
    }
    this.datasetKey = key;
    const cached = this.datasetCache.get(key);
    if (cached) {
      this.load(cached);
      return;
    }
    this.isLoading = true;
    this.cdr.markForCheck();
    this.http.get<QuizVideoMatchData>(dataset.url).subscribe(
      data => {
        this.datasetCache.set(key, data);
        if (this.datasetKey === key) {
          this.load(data);
        }
      },
      () => {
        if (this.datasetKey !== key) {
          return;
        }
        // A fresh checkout may only have the older run; fall back once before asking for a file.
        const fallback = QuizVideoCoveragePageComponent.datasets.find(d => d.key !== key);
        if (initial && fallback) {
          this.loadDataset(fallback.key, false);
          return;
        }
        this.isLoading = false;
        if (this.hasData) {
          this.loadError = `The ${key} results file is not available locally.`;
        }
        this.cdr.markForCheck();
      });
  }

  onFilePicked(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) {
      return;
    }
    file.text().then(text => {
      try {
        this.datasetKey = '';
        this.load(JSON.parse(text));
      } catch {
        this.loadError = 'That file is not a valid quiz-video-matches.json export.';
        this.cdr.markForCheck();
      }
    });
  }

  openLegend(): void {
    const dialog = this.legendDialog.nativeElement;
    dialog.showModal();
    // showModal focuses the first control (the close button); focus the dialog itself so no ring shows until the user tabs.
    dialog.focus();
  }

  selectLegend(item: CoverageLegendItem): void {
    this.legendItem = item;
  }

  closeLegend(): void {
    this.legendDialog.nativeElement.close();
  }

  /** A click on the dialog element itself (not its content) is a click on the backdrop. */
  onDialogClick(event: MouseEvent): void {
    if (event.target === this.legendDialog.nativeElement) {
      this.closeLegend();
    }
  }

  setTab(tab: CoverageTab): void {
    this.tab = tab;
  }

  toggleStatus(status: CoverageStatus): void {
    this.filter.status = this.filter.status === status ? null : status;
    this.tab = 'questions';
    this.applyFilter();
  }

  toggleSpelling(): void {
    this.filter.spelling = !this.filter.spelling;
    this.tab = 'questions';
    this.applyFilter();
  }

  onCourseChanged(): void {
    this.filter.moduleId = null;
    this.moduleOptions = this.allModuleOptions.filter(m => !this.filter.courseId || m.courseId === this.filter.courseId);
    this.applyFilter();
  }

  showModuleQuestions(module: CoverageModuleVm, status: CoverageStatus | null = null, spelling = false): void {
    this.filter.courseId = module.courseId;
    this.moduleOptions = this.allModuleOptions.filter(m => m.courseId === module.courseId);
    this.filter.moduleId = module.id;
    this.filter.status = status;
    this.filter.spelling = spelling;
    this.tab = 'questions';
    this.applyFilter();
  }

  clearFilters(): void {
    this.filter = { courseId: null, moduleId: null, status: null, spelling: false, search: '' };
    this.moduleOptions = this.allModuleOptions;
    this.applyFilter();
  }

  select(question: CoverageQuestionVm): void {
    if (question !== this.selected) {
      this.wordingOpen = false;
    }
    this.selected = question;
    this.draft = this.drafts.get(question.id) || null;
  }

  /** Asks the local drafting proxy for a spelling/grammar correction, a reword toward the lesson, or the lesson's wording. */
  draftFix(kind: DraftKind): void {
    const question = this.selected;
    if (!question || !question.draftInput) {
      return;
    }
    const state: DraftState = { kind, loading: true, error: '', versions: [], model: '' };
    this.drafts.set(question.id, state);
    this.draft = state;
    this.copiedKey = '';
    const body = {
      kind, ...question.draftInput,
      lesson: kind !== 'text' && question.fix ? question.fix.lessonName : undefined,
      excerpt: kind !== 'text' && question.fix ? question.fix.excerpt : undefined
    };
    this.http.post<QuizFixDraftResponse>(QuizVideoCoveragePageComponent.draftFixUrl, body).subscribe(
      res => {
        const lesson = question.fix ? question.fix.lessonName : 'the lesson';
        state.versions = res.versions.map((v, i) => {
          const percent = Math.round(v.check * 100);
          return {
            key: `${i}`,
            question: v.question,
            answers: v.answers.map((a, j) => ({ key: `${j}`, text: a.text, correct: a.correct })),
            reason: v.reason,
            checkLabel: kind === 'text' ? `TypeSafe: ${percent}% likely the meaning and correct answer are unchanged`
              : `TypeSafe: ${percent}% likely ${lesson} teaches the new correct answer`,
            checkOk: v.check >= QuizVideoCoveragePageComponent.draftCheckOk,
            copyText: [v.question, ...v.answers.map(a => `${a.correct ? '[correct] ' : ''}${a.text}`)].join('\n')
          };
        });
        state.model = res.model;
        state.loading = false;
        this.cdr.markForCheck();
      },
      err => {
        state.loading = false;
        state.error = err.status === 0
          ? 'The drafting helper is not running. Start it with: node scripts/quiz-builder-ai-proxy.js'
          : (err.error && err.error.error) || 'Drafting failed. Try again.';
        this.cdr.markForCheck();
      });
  }

  copyDraft(version: DraftVersionVm): void {
    navigator.clipboard.writeText(version.copyText).then(() => {
      this.copiedKey = version.key;
      this.cdr.markForCheck();
    });
  }

  /** Wording labels open the detail panel's wording breakdown; stops the list row's own select from closing it. */
  openWording(question: CoverageQuestionVm, event: Event): void {
    event.stopPropagation();
    this.selected = question;
    this.wordingOpen = true;
  }

  toggleWording(): void {
    this.wordingOpen = !this.wordingOpen;
  }

  applyFilter(): void {
    if (!this.view) {
      return;
    }
    // Status and spelling narrow the list but not the tile counts.
    const scoped = filterQuestions(this.view.questions, { ...this.filter, status: null, spelling: false });
    this.totals = countTotals(scoped);
    const percentLabel = (count: number) => {
      const percent = this.totals.total ? Math.round((count / this.totals.total) * 100) : 0;
      return count && !percent ? '<1%' : `${percent}%`;
    };
    this.kpis = this.statuses.map(s => ({
      key: s.key,
      label: s.label,
      icon: s.icon,
      count: this.totals[s.key],
      percentLabel: percentLabel(this.totals[s.key]),
      active: this.filter.status === s.key,
      title: `${s.label}: ${this.totals[s.key]} (${percentLabel(this.totals[s.key])}). ${s.description}`
    }));
    this.kpiGroups = COVERAGE_GROUPS.map(g => ({ ...g, items: this.kpis.filter(k => COVERAGE_STATUSES.find(s => s.key === k.key).group === g.key) }));
    this.stackLabel = `Coverage of ${this.totals.total} questions: ${this.kpis.map(k => k.title).join(', ')}`;
    this.spellingPercentLabel = percentLabel(this.totals.misspelled);
    this.questions = scoped.filter(q => (!this.filter.status || q.status === this.filter.status) && (!this.filter.spelling || q.misspelled));
    if (!this.questions.includes(this.selected)) {
      this.selected = this.questions[0] || null;
      this.draft = this.selected ? this.drafts.get(this.selected.id) || null : null;
    }
    const inScope = (courseId: number, moduleId: number) =>
      (!this.filter.courseId || courseId === this.filter.courseId) && (!this.filter.moduleId || moduleId === this.filter.moduleId);
    this.modules = this.view.modules.filter(m => inScope(m.courseId, m.id));
    this.videos = this.view.videos.filter(v => inScope(v.courseId, v.moduleId));
    this.untestedVideoCount = this.videos.filter(v => !v.strongCount).length;
    this.shownLabel = `${this.questions.length} of ${this.view.questions.length} questions`;
    this.cdr.markForCheck();
  }

  trackById(_: number, item: { id: number }): number {
    return item.id;
  }

  trackByKey(_: number, item: { key: string | number }): string | number {
    return item.key;
  }

  private load(data: QuizVideoMatchData): void {
    // Drafts and selection belong to the previous run's verdicts; do not carry them across datasets.
    this.drafts.clear();
    this.draft = null;
    this.selected = null;
    this.view = buildCoverageView(data);
    this.hasData = true;
    this.loadError = '';
    this.isLoading = false;
    this.meta = `${data.questions.length} questions · ${data.videos.length} videos · ${data.model} · generated ${new Date(data.generatedAt).toLocaleString()}`;
    this.courseOptions = [...data.courses]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(c => ({ key: c.courseId, label: c.name }));
    this.allModuleOptions = this.view.modules.map(m => ({ key: m.id, label: m.name, courseId: m.courseId }));
    this.moduleOptions = this.allModuleOptions;
    this.applyFilter();
  }
}
